import Compressor from "compressorjs";
import { uploadToS3 } from "./awsService";

function blobToFile(theBlob, fileName) {
  return new File([theBlob], fileName, {
    type: theBlob.type,
    lastModified: Date.now(),
  });
}

const compressImage = async (
  file,
  quality,
  maxWidth = 1080,
  maxHeight = 1920
) => {
  if (!file) {
    return;
  }

  let orientation;

  try {
    orientation = await getOrientation(file);
  } catch (e) {
    orientation = "portrait";
  }

  if (orientation === "landscape") {
    const t = maxWidth;
    maxWidth = maxHeight;
    maxHeight = t;
  }

  return new Promise((resolve, reject) => {
    new Compressor(file, {
      mimeType: "image/jpeg",
      strict: false,
      retainExif: false,
      convertTypes: "image/webp",
      maxWidth: maxWidth,
      maxHeight: maxHeight,
      quality: quality,
      success(result) {
        resolve(blobToFile(result, result.name));
      },
      error(err) {
        console.log(err.message);
        reject(err);
      },
    });
  });
};

const qualityTable = [
  { quality: 0.2, ratio: 0.98 },
  { quality: 0.4, ratio: 0.97 },
  { quality: 0.6, ratio: 0.96 },
  { quality: 0.8, ratio: 0.93 },
];

const getEstimatedQuality = (fileSize, desiredSize) => {
  const targetRatio = 1 - desiredSize / fileSize;
  let closestQuality = 1; // default
  let smallestDifference = Infinity;

  for (const entry of qualityTable) {
    const difference = Math.abs(targetRatio - entry.ratio);
    if (difference < smallestDifference) {
      smallestDifference = difference;
      closestQuality = entry.quality;
    }
  }

  return closestQuality;
};

export const compressImageFunction = async (
  file,
  enqueueSnackbar,
  desiredSizeTop = 600000,
  maxWidth,
  maxHeight
) => {
  if (!file) return;

  let quality = 0.8;
  if (file.size <= desiredSizeTop) {
    quality = getEstimatedQuality(file.size, desiredSizeTop);
  }

  let compressedFile = file;

  while (quality >= 0.2) {
    // 0.2 as the lowest quality threshold
    compressedFile = await compressImage(
      compressedFile,
      quality,
      maxWidth,
      maxHeight
    );

    if (compressedFile.size <= desiredSizeTop) {
      return compressedFile;
    }
    if (quality === 0.2) {
      return compressedFile;
    }

    quality -= 0.1; // reduce quality by 0.1 for the next iteration
  }

  // If after compressing to our lowest acceptable quality it's still over 1MB
  enqueueSnackbar("Error while compressing file.", {
    variant: "error",
  });

  return compressedFile;
};

const getOrientation = (file) => {
  return new Promise((resolve, reject) => {
    let _URL = window.URL || window.webkitURL;
    let img = new Image();

    img.onload = function () {
      let orientation = this.width < this.height ? "portrait" : "landscape";
      _URL.revokeObjectURL(img.src);

      // Resolve the promise with the orientation
      resolve(orientation);
    };

    img.onerror = () => {
      _URL.revokeObjectURL(img.src);

      // Reject the promise if there is an error
      reject(new Error("There was a problem loading the image."));
    };

    img.src = _URL.createObjectURL(file);
  });
};

export const uploadImageService = async (
  oldFile,
  enqueueSnackbar,
  uploadMedia,
  setMediaPublic,
  isEvent = false,
  skipCompression = false
) => {
  const reader = new FileReader();
  const file = skipCompression ? oldFile : await compressImageFunction(oldFile, enqueueSnackbar);

  reader.readAsDataURL(file);
  await new Promise((resolve) => (reader.onload = () => resolve()));
  try {
    // convert image file to base64 string
    const base64File = reader.result;

    if (base64File !== "") {
      const {
        data: {
          uploadMedia: { id, publicUrl, uploadUrl },
        },
      } = await uploadMedia({
        variables: {
          fileName: file.name,
          fileType: file.type,
          isEvent: isEvent,
        },
      });

      await uploadToS3(uploadUrl || "", base64File);

      if (isEvent === false) {
        await setMediaPublic({
          variables: { setMediaPublicId: id },
        });
      }

      return publicUrl;
    }
  } catch (error) {
    console.log(error);
    enqueueSnackbar("Could not upload photo!", {
      variant: "error",
    });
  }
};
